/* Shipping protection switch in the cart drawer (cart-protection component).
 * - The switch adds the protection with /cart/add.js or removes it with /cart/change.js and
 *   re-renders the drawer + cart icon through Dawn's section rendering.
 * - Auto-add (only when the card says data-auto-add="true"): when the drawer opens it adds the
 *   protection, until the shopper switches it off in this visit (sessionStorage).
 * - When the protection is the only thing left in the cart it is removed, so the cart is empty.
 * Works on Dawn 15 and 16 (cart-drawer element, 'active' class, sections cart-drawer +
 * cart-icon-bubble). */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const OPTED_OUT = 'cprot_user_removed';
  let busy = false;

  function renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const incoming = parsed.querySelector('cart-drawer');
      const current = document.querySelector('cart-drawer');
      if (incoming && current) {
        current.innerHTML = incoming.innerHTML;
        current.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
        // the overlay was just replaced: re-attach Dawn's click-outside-to-close, exactly as
        // Dawn's own renderContents does (cart-drawer.js) — without it the drawer can only be
        // closed with the X
        const overlay = current.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof current.close === 'function') overlay.addEventListener('click', current.close.bind(current));
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const incoming = parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
        bubble.innerHTML = incoming ? incoming.innerHTML : sections['cart-icon-bubble'];
      }
    }
  }

  async function toggle(button) {
    if (busy) return;
    busy = true;
    const isActive = button.getAttribute('aria-checked') === 'true';
    const variantId = button.dataset.variantId;
    const lineKey = button.dataset.lineKey;
    const card = button.closest('.cprot-card');
    try {
      if (isActive) sessionStorage.setItem(OPTED_OUT, '1');
      else sessionStorage.removeItem(OPTED_OUT);
    } catch (e) { /* private mode: no memory of the opt-out, the switch still works */ }

    button.setAttribute('aria-checked', String(!isActive));
    if (card) card.classList.toggle('cprot-card--active', !isActive);

    const remove = isActive && lineKey;
    const url = remove ? `${ROOT}cart/change.js` : `${ROOT}cart/add.js`;
    const body = remove
      ? { id: lineKey, quantity: 0, sections: SECTIONS.join(','), sections_url: window.location.pathname }
      : { items: [{ id: variantId, quantity: 1 }], sections: SECTIONS.join(','), sections_url: window.location.pathname };
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('cart request failed');
      renderSections((await res.json()).sections);
    } catch (err) {
      console.error('[cart-protection]', err);
      button.setAttribute('aria-checked', String(isActive));
      if (card) card.classList.toggle('cprot-card--active', isActive);
    } finally {
      busy = false;
    }
  }

  async function autoAdd() {
    if (busy) return;
    const button = document.querySelector('.cprot-toggle');
    if (!button || button.dataset.autoAdd !== 'true') return;
    try { if (sessionStorage.getItem(OPTED_OUT) === '1') return; } catch (e) { /* ignore */ }
    if (button.getAttribute('aria-checked') === 'true') return;
    await toggle(button);
  }

  async function removeIfAlone() {
    if (busy) return;
    const button = document.querySelector('.cprot-toggle');
    if (!button || button.getAttribute('aria-checked') !== 'true') return;
    const lineKey = button.dataset.lineKey;
    if (!lineKey) return;
    const variantId = parseInt(button.dataset.variantId, 10);
    let cart;
    try {
      const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      cart = await res.json();
    } catch (err) {
      return;
    }
    if (!cart.items.length || cart.items.some((it) => it.variant_id !== variantId)) return;
    // only the protection is left: take it out (not an opt-out — the shopper emptied the cart)
    busy = true;
    try {
      const res = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0, sections: SECTIONS.join(','), sections_url: window.location.pathname }),
      });
      if (res.ok) renderSections((await res.json()).sections);
    } catch (err) {
      console.error('[cart-protection]', err);
    } finally {
      busy = false;
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.cprot-toggle');
    if (!btn) return;
    e.preventDefault();
    toggle(btn);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // drawer opens → auto-add (only where the card allows it)
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) autoAdd();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    // drawer contents changed → protection alone in the cart?
    let timer = null;
    new MutationObserver(() => {
      if (busy) return;
      clearTimeout(timer);
      timer = setTimeout(removeIfAlone, 200);
    }).observe(drawer, { childList: true, subtree: true });
    setTimeout(removeIfAlone, 600);
  });
})();
